import React, { useState, useEffect } from "react";
import { Modal, Row, Col, Card } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { fetchApiData } from "../../redux/reducers/commonApiSlice";
import ReactVortoxSpinner from "../common-components/ReactVortoxSpinner";
import moment from "moment";
import "../../styles/tracking-system/break-details-modal.css";
import { toast } from "react-toastify";
import { BASE_URL } from "../../data/BaseUrl";
import { TrackingTimeFormat } from "./TrackingUtils";

const BreakDetailsModal = ({ show, onClose, breakLog, userInfoData }) => {
  const dispatch = useDispatch();
  const [workdayData, setWorkdayData] = useState(null);
  const [loading, setLoading] = useState(false);

  console.log("breakLog===>", breakLog);
  console.log("workdayData===>", workdayData);

  const detailDate =
    breakLog?.log_date || breakLog?.start_time
      ? moment(breakLog.start_time).format("DD-MM-YYYY")
      : moment().format("DD-MM-YYYY");

  // Fetch workday summary data
  useEffect(() => {
    const fetchWorkdayData = async () => {
      console.log("Starting to fetch workday data...");
      setLoading(true);
      try {
        // For now, using a mock user_id as mentioned in requirements
        // In real implementation, this would come from breakLog
        const userId = breakLog?.user_id;

        console.log("Fetching workday data for:", {
          userId,
          date: detailDate,
          token: userInfoData?.token,
        });

        // Use the full API URL as provided in requirements
        const apiUrl = `${BASE_URL}/user/workday/summary?user_id=${userId}&date=${detailDate}`;

        console.log("Making API call to:", apiUrl);
        console.log("BASE_URL from env:", process.env.REACT_APP_API_BASEURL);

        // Since this is a full URL, we might need to handle it differently
        // Try using fetch directly first to see if it works
        let result;
        try {
          console.log("Trying direct fetch...");
          const directResponse = await fetch(apiUrl, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${userInfoData.token}`,
              "Content-Type": "application/json",
            },
          });

          if (directResponse.ok) {
            result = { payload: await directResponse.json() };
            console.log("Direct fetch successful:", result);
          } else {
            throw new Error(`Direct fetch failed: ${directResponse.status}`);
          }
        } catch (directError) {
          console.log(
            "Direct fetch failed, trying with fetchApiData:",
            directError
          );
          // Fallback to fetchApiData
          result = await dispatch(
            fetchApiData({
              method: "GET",
              endpoint: apiUrl,
              token: userInfoData.token,
            })
          );
        }

        console.log("API result:", result);

        if (result?.payload?.status) {
          setWorkdayData(result.payload);
        } else {
          toast.error(result?.payload?.message);
        }
      } catch (error) {
        console.error("Error fetching workday data:", error);
        // Use mock data on error for demo purposes
        setWorkdayData({
          status: false,
          error: "Failed to fetch workday data",
        });
      } finally {
        setLoading(false);
      }
    };

    console.log("workdayData===>", workdayData);

    // Only fetch data when modal is shown and we have required data
    if (show && breakLog && userInfoData?.token) {
      console.log("Modal opened, fetching workday data...");
      fetchWorkdayData();
    } else {
      console.log("Modal not ready:", {
        show,
        breakLog: !!breakLog,
        hasToken: !!userInfoData?.token,
      });
    }
  }, [show, breakLog, userInfoData?.token, dispatch, detailDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper functions
  const getBreakTypeColor = (breakName) => {
    if (breakName === "out_of_range") return "#dc3545"; // Red
    if (breakName?.toLowerCase().includes("lunch")) return "#ffc107"; // Yellow
    return "#28a745"; // Green for regular breaks
  };

  const formatDuration = (duration) => {
    if (!duration) return "0m";
    const [hours, minutes] = duration.split(":");
    const h = parseInt(hours);
    const m = parseInt(minutes);

    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  const calculateTimelinePosition = (time, dayStart, dayEnd) => {
    const timeMinutes = moment(time).hour() * 60 + moment(time).minute();
    const startMinutes =
      moment(dayStart, "HH:mm").hour() * 60 +
      moment(dayStart, "HH:mm").minute();
    const endMinutes =
      moment(dayEnd, "HH:mm").hour() * 60 + moment(dayEnd, "HH:mm").minute();

    const totalDayMinutes = endMinutes - startMinutes;
    const elapsedMinutes = timeMinutes - startMinutes;

    return Math.max(0, Math.min(100, (elapsedMinutes / totalDayMinutes) * 100));
  };

  // Helper function to get the earliest and latest times from all shifts
  const getDayBounds = (shifts) => {
    if (!shifts || shifts.length === 0)
      return { dayStart: "00:00", dayEnd: "23:59" };

    const startTimes = shifts.map((shift) => shift.shift_start_time);
    const endTimes = shifts.map((shift) => shift.shift_end_time);

    const dayStart = startTimes.reduce((earliest, current) => {
      return moment(current, "HH:mm").isBefore(moment(earliest, "HH:mm"))
        ? current
        : earliest;
    });

    const dayEnd = endTimes.reduce((latest, current) => {
      return moment(current, "HH:mm").isAfter(moment(latest, "HH:mm"))
        ? current
        : latest;
    });

    return { dayStart, dayEnd };
  };

  const renderTimeline = () => {
    if (!workdayData?.breaks || !workdayData?.shifts) return null;

    const { shifts, breaks } = workdayData;
    const { dayStart, dayEnd } = getDayBounds(shifts);

    return (
      <div className="timeline-container">
        <div className="timeline-header">
          <h5>Workday Timeline</h5>
          <div className="timeline-legend">
            <div className="legend-item">
              <span
                className="legend-color"
                style={{ backgroundColor: "#007bff" }}
              ></span>
              <span>Work Shifts</span>
            </div>
            <div className="legend-item">
              <span
                className="legend-color"
                style={{ backgroundColor: "#28a745" }}
              ></span>
              <span>Regular Breaks</span>
            </div>
            <div className="legend-item">
              <span
                className="legend-color"
                style={{ backgroundColor: "#ffc107" }}
              ></span>
              <span>Meal Breaks</span>
            </div>
            <div className="legend-item">
              <span
                className="legend-color"
                style={{ backgroundColor: "#dc3545" }}
              ></span>
              <span>Out of Range</span>
            </div>
          </div>
        </div>

        <div className="timeline-main">
          <div className="timeline-time-labels">
            <span>{TrackingTimeFormat(dayStart)}</span>
            <span>{TrackingTimeFormat(dayEnd)}</span>
          </div>

          <div className="timeline-track">
            <div className="timeline-base"></div>

            {/* Render shift blocks */}
            {shifts.map((shift, index) => {
              const startPos = calculateTimelinePosition(
                shift.shift_start_time,
                dayStart,
                dayEnd
              );
              const endPos = calculateTimelinePosition(
                shift.shift_end_time,
                dayStart,
                dayEnd
              );
              const width = Math.max(2, endPos - startPos);

              return (
                <div
                  key={`shift-${index}`}
                  className="timeline-shift-block"
                  style={{
                    left: `${startPos}%`,
                    width: `${width}%`,
                    backgroundColor: "#007bff",
                    opacity: 0.3,
                    height: "100%",
                    position: "absolute",
                    borderRadius: "2px",
                  }}
                  title={`Shift ${index + 1}: ${TrackingTimeFormat(
                    shift.shift_start_time
                  )} - ${TrackingTimeFormat(shift.shift_end_time)}`}
                />
              );
            })}

            {/* Render break blocks */}
            {breaks.map((breakItem, index) => {
              const startPos = calculateTimelinePosition(
                breakItem.break_start_time,
                dayStart,
                dayEnd
              );

              const endTime =
                breakItem.break_end_time &&
                breakItem.break_end_time !== "00:00:00"
                  ? breakItem.break_end_time
                  : moment
                      .utc(breakItem.break_start_time)
                      .add(10, "minutes")
                      .toISOString();

              const endPos = calculateTimelinePosition(
                endTime,
                dayStart,
                dayEnd
              );

              const width = Math.max(2, endPos - startPos);
              const color = getBreakTypeColor(breakItem.break_name);

              return (
                <div
                  key={`break-${index}`}
                  className="timeline-break-block"
                  style={{
                    left: `${startPos}%`,
                    width: `${width}%`,
                    backgroundColor: color,
                    position: "absolute",
                    height: "100%",
                    borderRadius: "2px",
                    zIndex: 2,
                  }}
                  title={`${
                    breakItem.break_name === "out_of_range"
                      ? "⚠️ Out of Range"
                      : `☕ ${breakItem.break_name}`
                  } - Duration: ${formatDuration(
                    breakItem.break_duration_hms
                  )} | Time: ${TrackingTimeFormat(breakItem.break_start_time)}`}
                />
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderSummaryCards = () => {
    if (!workdayData?.summary) return null;

    const { summary } = workdayData;

    const cards = [
      {
        title: "Total Working Hours",
        value: formatDuration(summary.working_hours_hms),
        icon: "⏰",
        color: "#007bff",
      },
      {
        title: "Actual Working Hours",
        value: formatDuration(summary.actual_working_hours_hms),
        icon: "✅",
        color: "#28a745",
      },
      {
        title: "Total Breaks",
        value: summary.total_breaks,
        icon: "☕",
        color: "#ffc107",
      },
      {
        title: "Break Duration",
        value: formatDuration(summary.total_break_duration_hms),
        icon: "⏸️",
        color: "#6c757d",
      },
      {
        title: "Out of Range",
        value: summary.out_of_range_count,
        icon: "⚠️",
        color: "#dc3545",
      },
      {
        title: "Out of Range Duration",
        value: formatDuration(summary.out_of_range_duration_hms),
        icon: "🚫",
        color: "#dc3545",
      },
    ];

    return (
      <Row className="summary-cards">
        {cards.map((card, index) => (
          <Col md={4} sm={6} key={index} className="mb-3">
            <Card className="summary-card h-100">
              <Card.Body className="d-flex align-items-center">
                <div
                  className="summary-icon"
                  style={{
                    backgroundColor: `${card.color}20`,
                    color: card.color,
                  }}
                >
                  {card.icon}
                </div>
                <div className="summary-content">
                  <div className="summary-value">{card.value}</div>
                  <div className="summary-title">{card.title}</div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };
  // console.log("workdayData===>", workdayData);

  const renderBreaksList = () => {
    if (!workdayData?.breaks) return null;

    return (
      <div className="breaks-list">
        <h5>Break Details</h5>
        <div className="breaks-items">
          {workdayData.breaks.map((breakItem, index) => {
            const color = getBreakTypeColor(breakItem.break_name);
            const isOutOfRange = breakItem.break_name === "out_of_range";

            return (
              <div key={index} className="break-item">
                <div
                  className="break-indicator"
                  style={{ backgroundColor: color }}
                />
                <div className="break-content">
                  <div className="break-header">
                    <span className="break-name">
                      {isOutOfRange
                        ? "⚠️ Out of Range"
                        : `☕ ${breakItem.break_name}`}
                    </span>
                    <span className="break-duration">
                      {formatDuration(breakItem.break_duration_hms)}
                    </span>
                  </div>
                  <div className="break-time">
                    {TrackingTimeFormat(breakItem.break_start_time)} -{" "}
                    {TrackingTimeFormat(breakItem.break_end_time)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  console.log("Modal render state:", {
    show,
    breakLog,
    hasToken: !!userInfoData?.token,
  });

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      className="break-details-modal"
    >
      <Modal.Header closeButton className="premium-modal-header">
        <Modal.Title>
          <div className="modal-title-content">
            <div className="user-info">
              <h4>{workdayData?.name || breakLog?.user_name || "Employee"}</h4>
              <p>{workdayData?.gym_name || breakLog?.gym_name}</p>
            </div>
            <div className="date-info">
              <span className="date-badge">
                {/* {moment(breakLog?.start_time || new Date()).format(
                  "DD MMM YYYY"
                )} */}

                {detailDate}
              </span>
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="premium-modal-body">
        {loading ? (
          <div className="loading-container">
            <ReactVortoxSpinner />
            <p>Loading workday details...</p>
          </div>
        ) : workdayData?.status ? (
          <div className="workday-content">
            {/* Summary Cards */}
            {renderSummaryCards()}

            {/* Timeline Visualization */}
            <Row className="mt-4">
              <Col>
                <Card className="timeline-card">
                  <Card.Body>{renderTimeline()}</Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Break Details List */}
            <Row className="mt-4">
              <Col>
                <Card className="breaks-card">
                  <Card.Body>{renderBreaksList()}</Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Attendance Information */}
            <Row className="mt-4">
              <Col>
                <Card className="attendance-card">
                  <Card.Body>
                    <h5>Attendance & Shift Information</h5>
                    <div className="attendance-info">
                      <div className="attendance-item">
                        <span className="label">Check In:</span>
                        <span className="value">
                          {workdayData.attendance?.check_in_time || "-"}
                        </span>
                      </div>
                      <div className="attendance-item">
                        <span className="label">Check Out:</span>
                        <span className="value">
                          {workdayData.attendance?.check_out_time || "-"}
                        </span>
                      </div>
                    </div>

                    {/* Multiple Shifts Display */}
                    {workdayData.shifts && workdayData.shifts.length > 0 && (
                      <div className="shifts-info mt-3">
                        <h6
                          style={{
                            color: "#495057",
                            fontSize: "16px",
                            fontWeight: "600",
                            marginBottom: "15px",
                          }}
                        >
                          Assigned Shifts ({workdayData.shifts.length})
                        </h6>
                        <div
                          className="shifts-list"
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                          }}
                        >
                          {workdayData.shifts.map((shift, index) => (
                            <div
                              key={shift.shift_id || index}
                              className="shift-item"
                              style={{
                                backgroundColor: "#f8f9fa",
                                border: "1px solid #e9ecef",
                                borderRadius: "8px",
                                padding: "12px 16px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <div
                                className="shift-header"
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "4px",
                                }}
                              >
                                <span
                                  className="shift-number"
                                  style={{
                                    fontWeight: "600",
                                    color: "#007bff",
                                    fontSize: "14px",
                                  }}
                                >
                                  Shift {index + 1}
                                </span>
                                {shift.gym_name && (
                                  <small
                                    className="text-muted"
                                    style={{ fontSize: "12px" }}
                                  >
                                    {shift.gym_name}
                                  </small>
                                )}
                              </div>
                              <span
                                className="shift-time"
                                style={{
                                  fontWeight: "500",
                                  color: "#495057",
                                  fontSize: "14px",
                                  backgroundColor: "#fff",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  border: "1px solid #dee2e6",
                                }}
                              >
                                {moment(shift.shift_start_time, "HH:mm").format(
                                  "hh:mm A"
                                )}{" "}
                                -{" "}
                                {moment(shift.shift_end_time, "HH:mm").format(
                                  "hh:mm A"
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        ) : (
          <div className="error-container">
            <div className="error-icon">❌</div>
            <h5>Unable to Load Workday Data</h5>
            <p>
              There was an error loading the workday details. Please try again
              later.
            </p>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default BreakDetailsModal;
